import { Button, ConfigProvider, notification, theme } from "antd";
import es_ES from "antd/es/locale/es_ES";
import React, {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
	checkToken,
	fetchUserProfile,
	loadInitialData,
} from "./actions/authActions";
import withClearCache from "./ClearCache";
import { THEME } from "./constants";
import { getCurrentTheme, isUserAuthorized } from "./store/selectors";
import { getCookie, setCookie } from "./utils";

const MainContainer = lazy(() => import("./containers/MainContainer"));
const AuthContainer = lazy(() => import("./containers/AuthContainer"));

const App = () => {
	const dispatch = useDispatch();
	const currentTheme = useSelector(getCurrentTheme, shallowEqual);
	const [notificationApi, notificationContextHolder] = notification.useNotification();
	const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
	const [isInstallPopupVisible, setInstallPopupVisible] = useState(false);
	const [isAppInstalled, setAppInstalled] = useState(false);

	const ClearCacheComponent = withClearCache(() => {
		return <></>;
	});

	const { auth } = useSelector(isUserAuthorized) || {};

	const fetchInitialData = useCallback(() => {
		dispatch(fetchUserProfile());
		dispatch(loadInitialData());
	}, [dispatch]);

	const checkPersistency = useCallback(() => {
		dispatch(checkToken());
	}, [dispatch]);

	useEffect(() => {
		auth ? fetchInitialData() : checkPersistency();
	}, [auth, fetchInitialData, checkPersistency]);

	useEffect(() => {
		const isStandaloneMode = () =>
			window.matchMedia("(display-mode: standalone)").matches ||
			window.navigator.standalone === true ||
			document.referrer.includes("android-app://");

		setAppInstalled(isStandaloneMode());

		const handleBeforeInstallPrompt = (event) => {
			event.preventDefault();
			setDeferredInstallPrompt(event);
			setInstallPopupVisible(true);
		};

		const handleAppInstalled = () => {
			setAppInstalled(true);
			setDeferredInstallPrompt(null);
			setInstallPopupVisible(false);
			notificationApi.destroy("install-pwa");
		};

		window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
		window.addEventListener("appinstalled", handleAppInstalled);

		return () => {
			window.removeEventListener(
				"beforeinstallprompt",
				handleBeforeInstallPrompt
			);
			window.removeEventListener("appinstalled", handleAppInstalled);
		};
	}, [notificationApi]);

	const handleInstallApp = useCallback(async () => {
		if (!deferredInstallPrompt) return;

		deferredInstallPrompt.prompt();
		const { outcome } = await deferredInstallPrompt.userChoice;

		setDeferredInstallPrompt(null);
		setInstallPopupVisible(false);
		notificationApi.destroy("install-pwa");

		if (outcome === "accepted") {
			setAppInstalled(true);
		}
	}, [deferredInstallPrompt, notificationApi]);

	useEffect(() => {
		if (!deferredInstallPrompt || !isInstallPopupVisible || isAppInstalled) {
			notificationApi.destroy("install-pwa");
			return;
		}

		notificationApi.open({
			key: "install-pwa",
			message: "Instala Nutriplanner",
			description: "Puedes instalar la app en tu dispositivo para abrirla más rápido.",
			placement: "topRight",
			duration: 0,
			btn: (
				<Button type="primary" size="small" onClick={handleInstallApp}>
					Instalar app
				</Button>
			),
			onClose: () => setInstallPopupVisible(false),
		});
	}, [
		deferredInstallPrompt,
		handleInstallApp,
		isAppInstalled,
		isInstallPopupVisible,
		notificationApi,
	]);

	useEffect(() => {
		const refreshTheme = async () => {
			const theme = await getCookie(THEME);
			if (theme === undefined || theme === null) {
				await setCookie(THEME, true);
				await dispatch({ type: THEME, payload: true });
			} else {
				await dispatch({ type: THEME, payload: theme });
			}
		};
		refreshTheme();
	}, [dispatch]);

	// Aplicar clase de tema al body
	useEffect(() => {
		if (currentTheme) {
			document.body.classList.remove("dark-theme");
		} else {
			document.body.classList.add("dark-theme");
		}
	}, [currentTheme]);

	// Obtener el valor de la variable CSS --primary-color
	const primaryColor = useMemo(() => {
		return (
			getComputedStyle(document.documentElement)
				.getPropertyValue("--primary-color")
				.trim() || "#16a34a"
		);
	}, []);

	return (
		<Suspense>
			<ConfigProvider
				locale={es_ES}
				theme={{
					token: {
						borderRadius: 3,
						colorPrimary: primaryColor,
						colorTextDisabled: currentTheme
							? "rgba(0, 0, 0, 0.50)"
							: "rgba(255, 255, 255, 0.50)",
					},
					algorithm: currentTheme
						? theme.defaultAlgorithm
						: theme.darkAlgorithm,
				}}
			>
				{notificationContextHolder}
				<ClearCacheComponent />
				{auth ? <MainContainer /> : <AuthContainer />}
			</ConfigProvider>
		</Suspense>
	);
};

export default App;
